import type { CardType, WebSockets } from '@pushflow/types'
import { clsx } from 'clsx'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { currencySymbols } from '../../../constants'
import { hidden } from '../../../theme.css'
import type { EGameType, EHandName } from '../../../types/enums'
import { ECurrency } from '../../../types/enums'
import { getCurrencyFractionDigits, roundFloatTo } from '../../../utils'
import { CoinPile } from '../'
import { Money } from '../Money'
import { CardBoard } from './components'
import {
  boardsContainer,
  pot,
  potPile,
  potPilesContainer,
  potSumValue,
  potValue,
  secondBoardHandName,
  secondBoardOffset,
  tableInfo,
} from './index.css'

export const TableContent = (props: {
  gameType: EGameType
  currency: ECurrency
  smallBlind: number
  bigBlind: number
  valuesInBigBlinds: boolean
  code: string
  name: string | null
  cards: string | null
  secondBoard: string | null
  pots: WebSockets.PotType[]
  handNumber: number
  secondBoardHandName?: EHandName | null
  isSnapshot?: boolean
  onNewCards?: (count: number) => void
}) => {
  const { t } = useTranslation()

  const potSummary = useMemo(
    () =>
      roundFloatTo(
        props.pots.reduce((sum, pot) => sum + pot.amount, 0),
        Math.min(getCurrencyFractionDigits(props.currency), 4),
      ),
    [props.pots],
  )

  const currency = props.currency === ECurrency.NONE ? '' : props.currency
  const primary = props.cards ? new Set(props.cards.split(' ')) : null
  const secondBoard =
    primary && props.secondBoard
      ? (props.secondBoard
          .split(' ')
          .map((card) => (primary.has(card) ? 'empty' : card)) as (
          | CardType
          | 'empty'
        )[])
      : []

  return (
    <>
      <div className={tableInfo}>
        <span>№ {props.handNumber}</span>
        <span>
          {props.name || props.code} — {t(`table.gameType.${props.gameType}`)}
        </span>
        <span>
          {`${currencySymbols[props.currency]}${props.smallBlind}/${props.bigBlind} ${currency}`}
        </span>
      </div>

      <div className={potPilesContainer}>
        {props.pots.map((pot) => {
          if (!pot.amount) {
            return null
          }
          const contributions = pot.eligiblePlayers.map(
            (player) => player.contribution,
          )
          return (
            <div
              key={pot.id}
              className={potPile}
            >
              <CoinPile
                contributions={contributions}
                bigBlind={props.bigBlind}
              />
              {/* <span
                className={clsx(
                  potValue,
                  props.pots.length > 1 ? null : hidden,
                )}
              >
                <Money
                  inBigBlinds={props.valuesInBigBlinds}
                  bigBlind={props.bigBlind}
                >
                  {pot.amount}
                </Money>
              </span> */}
            </div>
          )
        })}
      </div>

      {potSummary === 0 ? null : (
        <span className={pot}>
          <span>{t('table.label.pot')}:</span>
          <span className={potSumValue}>
            <Money
              inBigBlinds={props.valuesInBigBlinds}
              bigBlind={props.bigBlind}
              isAnimated={props.isSnapshot}
            >
              {potSummary}
            </Money>
          </span>
        </span>
      )}

      <div className={boardsContainer}>
        <CardBoard
          cards={
            props.cards?.length
              ? (props.cards.split(' ') as (CardType | 'null')[])
              : []
          }
          onNewCards={props.onNewCards}
        />

        {secondBoard ? (
          <CardBoard
            cards={secondBoard}
            className={secondBoardOffset}
            shadow={true}
            onNewCards={props.onNewCards}
          />
        ) : null}
        {props.secondBoardHandName ? (
          <span className={secondBoardHandName}>
            {t(`table.handName.${props.secondBoardHandName}`)}
          </span>
        ) : null}
      </div>
    </>
  )
}
